import { useEffect, useState } from "react";
import { captureException } from "@sentry/nextjs";
import { getFormDataById, getSessionLearners, getSessionResponses } from "../apis";
import { resolveLoadErrorMessage, V2FormData, V2Question } from "../helper";
import { GridAnswers, Learner, LearnerStatusMap, TabularData } from "./types";
import { mergeAnswers, readCachedAnswers, storageKey } from "./storage";

interface UseTabularDataParams {
  formId: string;
  sessionId: string;
  tenantId?: string;
}

// Loads the grid's data: questions, learners, and existing feedback (merged under any cached draft).
export const useTabularData = ({ formId, sessionId, tenantId }: UseTabularDataParams): TabularData => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formType, setFormType] = useState<string | undefined>(undefined);
  const [legacyId, setLegacyId] = useState<string | null | undefined>(undefined);
  const [questions, setQuestions] = useState<V2Question[]>([]);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [initialAnswers, setInitialAnswers] = useState<GridAnswers>({});
  const [statusByLearner, setStatusByLearner] = useState<LearnerStatusMap>({});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [formRes, learnersRes, responsesRes] = await Promise.all([
          getFormDataById(formId),
          getSessionLearners(sessionId),
          getSessionResponses(formId, sessionId).catch(() => null) // prefill is best-effort
        ]);

        if (cancelled) return;

        const form: V2FormData | null = formRes?.data || null;
        const sortedQuestions = [...(form?.questions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const learnerList: Learner[] = learnersRes?.data || [];

        // Server prefill → answers[learnerId][questionId] and a per-learner status map.
        const serverAnswers: GridAnswers = {};
        const status: LearnerStatusMap = {};
        const responsesByLearner: Record<string, any> = responsesRes?.data || {};
        Object.keys(responsesByLearner).forEach(learnerId => {
          const entry = responsesByLearner[learnerId] || {};
          status[learnerId] = entry.status ?? null;
          serverAnswers[learnerId] = {};
          (entry.answers || []).forEach((answer: { questionId: string; answer: unknown }) => {
            serverAnswers[learnerId][answer.questionId] = answer.answer as never;
          });
        });

        const cached = readCachedAnswers(storageKey(tenantId, formId, sessionId));

        setFormTitle(form?.title || "");
        setFormType(form?.type);
        setLegacyId(form?.metadata?.legacyId);
        setQuestions(sortedQuestions);
        setLearners(learnerList);
        setStatusByLearner(status);
        setInitialAnswers(mergeAnswers(serverAnswers, cached));
      } catch (err) {
        captureException(err);
        if (!cancelled) setError(resolveLoadErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (formId && sessionId) {
      load();
    } else {
      setError("Missing form or session information.");
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [formId, sessionId, tenantId]);

  return { loading, error, formId, formTitle, formType, legacyId, questions, learners, initialAnswers, statusByLearner };
};
